import type { ReactNode } from 'react';

import { useNavigate, useRouteLoaderData } from '@remix-run/react';
import type { StripeCardElementComponent, TPricingElement } from '@shared/dream-components';
import { CardElement, Elements, PaymentElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';

import SignupAction from '~/components/SignupAction';
import UpgradeAction from '~/components/UpgradeAction';
import { useUpgradeContext } from '~/context/useUpgradeContext';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { RootLoaderData } from '~/root';

import { getActiveSelection } from './Pricing.helpers';

export interface PricingCheckoutFields {
  // A free tier or a cardless offer subscribes through signup, so it has no payment fields.
  isSignup: boolean;
  isSubmitting: boolean;
  setEmail: (email: string) => void;
  StripeCardElement?: StripeCardElementComponent;
  StripePaymentElement?: typeof PaymentElement;
  PaymentRequestButtonElement?: ReactNode;
}

interface Props {
  node: TPricingElement;
  // Shown until Stripe is ready to build its payment fields.
  fallback: ReactNode;
  onSuccess?: (ssoLoginUrl?: string) => void;
  successRedirectTo?: string;
  forceStripePaymentElement?: boolean;
  children: (fields: PricingCheckoutFields) => ReactNode;
}

// Pays for the upgrade context's selection: the actions, Stripe and the redirects after. The caller draws the fields.
const PricingCheckout = ({
  node,
  fallback,
  onSuccess,
  successRedirectTo,
  forceStripePaymentElement,
  children,
}: Props) => {
  const navigate = useNavigate();
  const { nextPage, nextStep } = useSignupRedirects();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site.publication;
  const contextValue = useUpgradeContext();
  const activeSelection = getActiveSelection(contextValue);

  const canUseStripePaymentElement = forceStripePaymentElement || publication?.uses_stripe_payment_element;
  const canShowPaymentRequestButton =
    node.attrs.internalCheckout === true ? publication?.stripe_payment_method_domain_enabled : false;
  const needsElementsOptions = !!canUseStripePaymentElement && node.attrs.internalCheckout === true;

  // Handles the success of the upgrade action
  // 1. Redirect to the selected tier's redirect_url if it is defined
  // 2. Redirect to the next page if there is no redirect_url (signup flow)
  const handleSignupSuccess = () => {
    if (onSuccess) {
      onSuccess();
      return;
    }

    const tierRedirectUrl = activeSelection.tier?.redirect_url;

    if (tierRedirectUrl && typeof window !== 'undefined') {
      window.location.href = tierRedirectUrl;
      return;
    }

    nextPage();
  };

  // If the user has selected a free tier
  // or a price with premium offer that does
  // not require payment (while internal checkout is enabled)
  // then let them signup using signup action.
  //
  // Gated to individual mode: gift/group are inherently paid (card required),
  // so they must use the Stripe path below. Without this gate the form-mode
  // tabs render inert on first load whenever the default individual tier is
  // free — the user can't switch to Group until they pick a paid tier first.
  if (
    contextValue.currentFormMode === 'individual' &&
    ((contextValue.isInternalCheckout && contextValue.selectedTier?.id === 'free') ||
      (contextValue.isInternalCheckout &&
        contextValue.selectedPrice?.premium_offer &&
        !contextValue.selectedPrice?.premium_offer?.card_required))
  ) {
    return (
      <SignupAction
        id={node.attrs.id}
        premiumOfferId={contextValue.selectedPrice?.premium_offer?.id}
        redirectUrl={contextValue.selectedTier?.redirect_url}
        shouldSkipRedirectsAfterSuccess
        shouldSkipAutoLogIn
        onSuccess={() => {
          handleSignupSuccess();
        }}
      >
        {({ isLoading, setEmail }) => children({ isSignup: true, isSubmitting: isLoading, setEmail })}
      </SignupAction>
    );
  }

  // Handles the success of the upgrade action
  // 1. Redirect to the selected tier's redirect_url if it is defined
  // 2. Redirect to the next step if it is defined (signup flow)
  // 3. Redirect to the login URL if it is provided (SSO login)
  const handleSuccess = (loginUrl?: string) => {
    if (onSuccess) {
      onSuccess(loginUrl);
      return;
    }

    const tierRedirectUrl = activeSelection.tier?.redirect_url;
    if (tierRedirectUrl && typeof window !== 'undefined') {
      window.location.href = tierRedirectUrl;
      return;
    }

    if (nextStep) {
      nextPage();
      return;
    }

    navigate(loginUrl || '/');
  };

  // react-stripe-js builds the Elements group once; `mode` can't be added afterwards.
  const isWaitingForStripeSession = !contextValue.stripeSession;
  const isWaitingForElementsOptions = needsElementsOptions && !contextValue.stripeElementsOptions;

  if (isWaitingForStripeSession || isWaitingForElementsOptions) {
    return <>{fallback}</>;
  }

  return (
    <Elements
      stripe={contextValue.stripeSession}
      options={canUseStripePaymentElement ? contextValue.stripeElementsOptions : undefined}
    >
      <UpgradeAction
        canShowPaymentRequestButton={canShowPaymentRequestButton}
        onSuccess={handleSuccess}
        successRedirectTo={successRedirectTo}
        isCollectingBillingAddress={node.attrs.collectBillingAddress}
        forceStripePaymentElement={forceStripePaymentElement}
      >
        {({ isSubmitting, paymentRequest, setEmail }) =>
          children({
            isSignup: false,
            isSubmitting,
            setEmail,
            StripeCardElement:
              !canUseStripePaymentElement && node.attrs.internalCheckout
                ? (CardElement as StripeCardElementComponent)
                : undefined,
            StripePaymentElement:
              canUseStripePaymentElement && node.attrs.internalCheckout ? PaymentElement : undefined,
            PaymentRequestButtonElement:
              !canUseStripePaymentElement && canShowPaymentRequestButton && paymentRequest ? (
                <PaymentRequestButtonElement options={{ paymentRequest }} />
              ) : undefined,
          })
        }
      </UpgradeAction>
    </Elements>
  );
};

export default PricingCheckout;
